<template>
    <div>
        <h5 class="title-2" :class="marginClass">{{ $t("surgery.postsurgery") }}</h5>
        <h2 class="title" :class="marginClass">{{ $t('surgery.complications.title') }}</h2>
        <hr class="divider">

        <radio-checkbox-field
            :class="marginClass"
            :field="{label: $t('surgery.complications.hasComplications')}"
            v-model="hasComplications"
            field-name="has-complications"
        />

        <div v-if="hasComplications" :class="marginClass" class="mb-3">
            <radio-checkbox-field
                id="has-transfusions"
                class="ml-4"
                :field="{label: $t('surgery.complications.hasTransfusions')}"
                v-model="hasTransfusions"
                field-name="has-transfusions"
            />

            <div v-if="hasTransfusions" class="ml-5" >
                <div class="mb-2" v-for="(choice, index) in $v.transfusions.$each.$iter" :key="index">
                    <b-form-checkbox
                        class="align-baseline mt-2"
                        :id="'tranfusion-' + index"
                        v-model="choice.selected.$model"
                        :name="'tranfusion-' + index"
                    >
                        {{ transfusionFields.transfusion_type.choices[index].display_name }}
                    </b-form-checkbox>
                    <div v-if="choice.selected.$model" class="ml-5">
                        <date-field
                            v-model="choice.date.$model"
                            :field="transfusionFields.date"
                            :field-name="'tranfusion-date-' + index"
                            :errors="choice.date.$error ? {['tranfusion-date-' + index]: [$t('error.required')] } : {}"
                            class="mb-3"
                            datetime
                        />
                        <input-field
                            v-model="choice.amount.$model"
                            :field="{label: transfusionFields.amount.label }"
                            :field-name="'tranfusion-amount-' + index"
                            :disabled="!choice.selected.$model"
                            :errors="choice.amount.$error ? {['tranfusion-amount-' + index]: [$t('error.required')] } : {}"
                            :unit="$tc('utils.unit', 2)"
                            class="w-auto d-inline-block mb-3"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="hasComplications"
            :class="marginClass"
        >
            <radio-checkbox-field
                class="ml-4 mb-3"
                :field="{label: $t('surgery.complications.hasTreatments')}"
                v-model="hasTreatments"
                field-name="has-treatments"
            />

            <div v-if="hasTreatments" class="ml-5">
                <div
                    v-for="(treatment, index) in $v.treatments.$each.$iter"
                    :key="index"
                    class="my-3 p-3 rounded border border-primary"
                >
                    <multiple-card-item
                        :title="$t('surgery.complications.treatment')"
                        @delete="removeTreatment(parseInt(index))"
                    >
                        <date-field
                            :field="novoeightTreatmentFields.date"
                            v-model="treatment.date.$model"
                            :field-name="'dose-date' + index"
                            :label="$t('utils.date')"
                            :errors="treatment.date.$error ? {['dose-date' + index]: [$t('error.required')] } : {}"
                            datetime
                        />
                        <input-field
                            v-model="treatment.total_dose.$model"
                            :field="novoeightTreatmentFields.total_dose"
                            :field-name="'total-dose-' + index"
                            :errors="treatment.total_dose.$error ? {['total-dose-' + index]: [$t('error.required')] } : {}"
                            :unit="'fake'"
                        >
                            <template v-slot:unit>
                                {{ novoeightTreatmentFields.total_dose.help_text }} |
                                {{ calculateDosePerKg(treatment.total_dose.$model, weight) | numberOrEmpty }}
                                {{ $t('utils.FVIIPerKgUnit') }}
                            </template>
                        </input-field>
                    </multiple-card-item>
                </div>

                <hemocir-button
                    :title="$t('utils.add')"
                    font-awesome-icon="fa-plus-circle"
                    variant="outline-gray"
                    @click="addTreatment"
                />
            </div>
        </div>

    </div>
</template>

<script>
    import {mapState} from "vuex";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import SurgeryAddElementMixin from "@/components/mixins/NavigableComponentMixin";
    import RadioCheckboxField from "@/components/fields/RadioCheckboxField";
    import {requiredIf} from "vuelidate/lib/validators";
    import TransfusionMixin from "@/components/surgery/create/TransfusionMixin";
    import InputField from "@/components/fields/InputField";
    import DateField from "@/components/fields/DateField";
    import {isArray} from "lodash";
    import {calculateDosePerKg} from "@/components/surgery/SurgeryUtils";
    import MultipleCardItem from "@/components/MultipleCardItem";
    import ScrollToErrorMixin from "@/components/mixins/ScrollToErrorMixin";

    export default {
        name: "SurgeryComplications",
        components: {
            MultipleCardItem,
            DateField,
            InputField,
            RadioCheckboxField
        },
        mixins: [SurgeryAddElementMixin, TransfusionMixin, ScrollToErrorMixin],
        data() {
            return {
                hasComplications: false,
                hasTreatments: false,
                treatments: [],
            };
        },
        computed: {
            ...mapState([
                'surgeryFields',
                'transfusionFields',
                'novoeightTreatmentFields',
            ]),
            ...mapStateProperty([
                'surgery.weight',
                {path: 'surgery.complication_transfusions', name: 'surgeryTransfusions'},
                {path: 'surgery.complication_treatments', name: 'surgeryTreatments'},
            ]),
            marginClass() {
                return 'mx-4';
            },
        },
        watch: {
            hasTreatments(newValue) {
                if (newValue) {
                    if (this.treatments.length === 0) {
                        this.addTreatment();
                    }
                }
            },
        },
        created() {
            this.transfusions = this.transfusionCreateTransfusions(this.surgeryTransfusions, this.transfusionFields);
            this.hasTransfusions = this.transfusionHasTransfusions(this.surgeryTransfusions);

            this.treatments = [];
            if (isArray(this.surgeryTreatments) && this.surgeryTreatments.length > 0) {
                this.hasTreatments = true;
                this.treatments = this.surgeryTreatments;
            }

            this.hasComplications = this.hasTransfusions || this.hasTreatments;
        },
        methods: {
            back() {
                this.storeTransfusions();
                this.storeTreatments();
            },
            validate() {
                if (!this.hasComplications) {
                    this.treatments = [];
                    this.transfusions = [];
                }

                this.$v.$touch();
                const isValid = !this.$v.$invalid;
                if (isValid) {
                    this.storeTransfusions();
                    this.storeTreatments();
                } else {
                    this.$nextTick(this.scrollToError);
                }
                return isValid;
            },
            storeTransfusions() {
                this.surgeryTransfusions = this.transfusionGetTransfusionForStoring().map(transfusion => {
                    return {
                        ...transfusion,
                        is_complication: true,
                    };
                });
            },
            storeTreatments() {
                let getTreatments = () => {
                    let treatments = [];
                    if (this.hasTreatments) {
                        this.treatments.forEach(treatment => {
                            treatments.push({
                                date: treatment.date,
                                total_dose: treatment.total_dose,
                                is_complication: true,
                            });
                        });
                    }
                    return treatments;
                };

                this.surgeryTreatments = getTreatments();
            },
            addTreatment() {
                this.treatments.push({
                    date: null,
                    total_dose: null,
                });
            },
            removeTreatment(index) {
                this.treatments.splice(index, 1);
            },
            calculateDosePerKg,
        },
        validations: {
            transfusions: {
                $each: {
                    selected: {},
                    amount: {
                        required: requiredIf(function (transfusion) {
                            return this.hasTransfusions && transfusion.selected;
                        })
                    },
                    date: {
                        required: requiredIf(function (transfusion) {
                            return this.hasTransfusions && transfusion.selected;
                        }),
                    }
                }
            },
            treatments: {
                $each: {
                    date: {
                        required: requiredIf(function () {
                            return this.hasTreatments;
                        }),
                    },
                    total_dose: {
                        required: requiredIf(function () {
                            return this.hasTreatments;
                        }),
                    }
                }
            }
        },
    };
</script>

<style scoped>
</style>